import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useGetAdminTransactions } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Download } from "lucide-react";

function fmt(ts: string) {
  return new Date(ts).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const TYPE_COLOURS: Record<string, string> = {
  "Lead Unlock":   "bg-blue-100 text-blue-700",
  "Deal Listing":  "bg-amber-100 text-amber-700",
  "Event Listing": "bg-purple-100 text-purple-700",
};

export default function AdminTransactions() {
  const { user, isLoading: authLoading } = useAuth();
  const [search, setSearch] = useState("");

  const { data, isLoading } = useGetAdminTransactions({ limit: 500, search: search || undefined });

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const items = data?.items ?? [];

  const totalRevenue = items.reduce((s, t) => s + (t.amount ?? 0), 0);

  const downloadCsv = () => {
    const rows = [
      ["ID", "Type", "Description", "Amount (£)", "Transaction ID", "User", "Email", "Date", "Status"],
      ...items.map((t) => [
        t.id,
        t.type,
        t.description ?? "",
        t.amount != null ? t.amount.toFixed(2) : "",
        t.transactionId ?? "",
        t.userName ?? "",
        t.userEmail ?? "",
        t.createdAt,
        t.status,
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "transactions.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout current="/admin/transactions">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-primary">Transactions</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {data?.total ?? 0} transactions · £{totalRevenue.toFixed(2)} total revenue
            </p>
          </div>
          <button
            onClick={downloadCsv}
            className="flex items-center gap-2 text-sm font-medium text-slate-600 border border-slate-200 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>

        <Input
          placeholder="Search by description, user, email or transaction ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
        />

        {isLoading ? (
          <div className="text-muted-foreground text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-4xl mb-3">💳</p>
              <p className="text-muted-foreground">
                {search ? "No transactions match your search." : "No transactions yet."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Type</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Description</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Amount</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Transaction ID</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">User</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Email</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Date</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((t, i) => (
                      <tr key={`${t.type}-${t.id}`} className={`border-b border-slate-50 ${i % 2 === 0 ? "" : "bg-slate-50/50"}`}>
                        <td className="px-4 py-3">
                          <span className={`inline-flex text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_COLOURS[t.type] ?? "bg-slate-100 text-slate-600"}`}>
                            {t.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-800 max-w-[200px] truncate">
                          {t.description ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-700 font-medium">
                          {t.amount != null ? `£${t.amount.toFixed(2)}` : "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-400 font-mono text-xs max-w-[140px] truncate" title={t.transactionId ?? ""}>
                          {t.transactionId ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{t.userName ?? "—"}</td>
                        <td className="px-4 py-3 text-slate-500">{t.userEmail ?? "—"}</td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmt(t.createdAt)}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                            {t.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
